import {useLayoutEffect, useRef, useMemo} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {RootState} from '../../../../../index';
import {getRewardsState} from '../../../../../selectors/fortnite-game/rewardsSelector';
import {RewardsState} from '../../../../../reducers/fortnite-game/rewards.reducer';
import rewardsActions from '../../../../../actions/fortnite-game/rewards.actions';
import {UseTabDataParams} from '../types';

export interface UseRewardsState extends RewardsState {
    reload: () => void;
}

const useRewardsData = ({islandCode, startDate, endDate}: UseTabDataParams): UseRewardsState => {
    const dispatch = useDispatch();
    const intervalRef = useRef(null);

    const {data, loading, error} = useSelector<RootState, RewardsState>(
        getRewardsState,
        shallowEqual
    );

    const formattedData = useMemo(() => {
        const engagementPayouts = data.map(({time, engagement}) => ({time, engagement}));
        const combinedPayouts = data.map(({time, engagement, sac}) => ({
            time,
            engagement,
            sac,
            total: Number(engagement) + Number(sac)
        }));

        return islandCode ? engagementPayouts : combinedPayouts;
    }, [data, islandCode, startDate, endDate]);

    const defaultQueryParams = {
        fromTs: startDate.valueOf(),
        toTs: endDate.valueOf()
    };

    const queryParams = islandCode ? {...defaultQueryParams, islandCode} : defaultQueryParams;

    const fetchData = () => {
        dispatch(rewardsActions.getRewards(queryParams));
    };

    useLayoutEffect(() => {
        fetchData();

        return () => {
            clearInterval(intervalRef.current);
        };
    }, [islandCode, startDate, endDate]);

    return {
        data: formattedData,
        loading,
        error,
        reload: fetchData
    };
};

export default useRewardsData;
